import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useReports } from '../hooks/useReports'
import { DayPicker } from '../components/DayPicker'
import { ErrorAlert } from '../components/ErrorAlert'
import { reportTemplates } from '../data/reportTemplates'

export function CreateReport() {
  const navigate = useNavigate()
  const { loading, error, createReport } = useReports()

  const [formData, setFormData] = useState({
    name: '',
    prompt: '',
    days_of_week: []
  })
  const [formErrors, setFormErrors] = useState({})
  const [selectedTemplate, setSelectedTemplate] = useState(null)

  function handleTemplateSelect(template) {
    setSelectedTemplate(template.id)
    setFormData({
      ...formData,
      name: template.reportName,
      prompt: template.prompt
    })
    // Clear any existing errors when template is selected
    setFormErrors({})
  }

  function validateForm() {
    const errors = {}

    // Validate name
    if (!formData.name.trim()) {
      errors.name = 'Report name is required'
    } else if (formData.name.trim().length < 3) {
      errors.name = 'Report name must be at least 3 characters'
    } else if (formData.name.length > 100) {
      errors.name = 'Report name must be less than 100 characters'
    }

    // Validate prompt
    if (!formData.prompt.trim()) {
      errors.prompt = 'Prompt is required'
    } else if (formData.prompt.trim().length < 20) {
      errors.prompt = 'Prompt must be at least 20 characters'
    } else if (formData.prompt.length > 5000) {
      errors.prompt = 'Prompt must be less than 5000 characters'
    }

    // Validate days
    if (formData.days_of_week.length === 0) {
      errors.days_of_week = 'Select at least one day'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  async function handleSubmit(e) {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      await createReport(formData)
      navigate('/')
    } catch (err) {
      console.error('Failed to create report:', err)
    }
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Create New Report</h1>
        <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
          Define a report that will be automatically generated and emailed on your schedule
        </p>
      </div>

      {error && (
        <div className="mb-6">
          <ErrorAlert message={error} />
        </div>
      )}

      {/* Template Selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Choose a Template (Optional)</h2>
        <p className="text-sm text-gray-600 mb-4">
          Start with a pre-configured report template, then customize as needed
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {reportTemplates.map((template) => (
            <button
              key={template.id}
              type="button"
              onClick={() => handleTemplateSelect(template)}
              className={`text-left p-4 rounded-lg border-2 transition-all ${
                selectedTemplate === template.id
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
            >
              <div className="font-semibold text-gray-900">{template.name}</div>
              <div className="text-xs text-gray-500 mt-1">{template.reportName}</div>
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        {/* Report Name */}
        <div className="mb-6">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Report Name {formErrors.name && <span className="text-red-600 text-sm">*</span>}
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            maxLength={100}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              formErrors.name ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="e.g., Weekly Financial Analysis"
          />
          <div className="mt-1 flex justify-between">
            {formErrors.name ? (
              <p className="text-sm text-red-600">{formErrors.name}</p>
            ) : (
              <span className="text-sm text-gray-500">3-100 characters</span>
            )}
            <span className="text-sm text-gray-500">{formData.name.length}/100</span>
          </div>
        </div>

        {/* Prompt */}
        <div className="mb-6">
          <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-2">
            Report Prompt {formErrors.prompt && <span className="text-red-600 text-sm">*</span>}
          </label>
          <textarea
            id="prompt"
            value={formData.prompt}
            onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
            maxLength={5000}
            rows={8}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              formErrors.prompt ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Describe what information you want in your report, including desired format and structure..."
          />
          <div className="mt-1 flex justify-between">
            {formErrors.prompt ? (
              <p className="text-sm text-red-600">{formErrors.prompt}</p>
            ) : (
              <p className="text-sm text-gray-500">
                Be specific about topics, sources, and desired format (20-5000 characters)
              </p>
            )}
            <span className={`text-sm ${formData.prompt.length > 4800 ? 'text-orange-600 font-medium' : 'text-gray-500'}`}>
              {formData.prompt.length}/5000
            </span>
          </div>
        </div>

        {/* Day Picker */}
        <div className="mb-6">
          <DayPicker
            selectedDays={formData.days_of_week}
            onChange={(days) => setFormData({ ...formData, days_of_week: days })}
            error={formErrors.days_of_week}
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-4 py-3 sm:py-2 text-gray-700 hover:text-gray-900 font-medium text-sm sm:text-base"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 sm:py-2 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            {loading ? 'Creating...' : 'Create Report'}
          </button>
        </div>
      </form>
    </div>
  )
}
